/**
 * Shared size matching for product switches.
 *
 * Size titles are free text per Size row in the backend ("30 x 40 cm",
 * "30x40cm", "30 × 40", "30,5 x 40 cm" …) and two products rarely reference the
 * identical string. A raw `===` on the title therefore fails and the editor
 * silently jumps to the first/default size. This helper matches by exact
 * title, then by normalized title, then by physical dimensions (with
 * orientation tolerance).
 */

export type SizeLike = {
  id?: string | number | null
  title?: string | null
  variantId?: string | number | null
  widthMm?: number | null
  heightMm?: number | null
  widthCm?: number | null
  heightCm?: number | null
  isDefault?: boolean | null
}

export function normalizeSizeTitle(title: unknown): string {
  return String(title ?? '')
    .toLowerCase()
    .replace(/×/g, 'x')
    .replace(/,/g, '.')
    .replace(/\s*cm\s*/g, '')
    .replace(/\s+/g, '')
    .replace(/(\d)\.0+(?=x|$)/g, '$1')
    .trim()
}

/** Dimensions in mm from explicit fields or parsed out of the title. */
export function sizeDimsMm(opt: SizeLike | null | undefined): { widthMm: number; heightMm: number } | null {
  if (!opt) return null
  const wMm = Math.round(Number(opt.widthMm || 0))
  const hMm = Math.round(Number(opt.heightMm || 0))
  if (wMm > 0 && hMm > 0) return { widthMm: wMm, heightMm: hMm }

  const wCm = Number(opt.widthCm || 0)
  const hCm = Number(opt.heightCm || 0)
  if (wCm > 0 && hCm > 0) return { widthMm: Math.round(wCm * 10), heightMm: Math.round(hCm * 10) }

  const m = String(opt.title || '').replace(/,/g, '.').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i)
  if (m) {
    const w = Math.round(parseFloat(m[1]) * 10)
    const h = Math.round(parseFloat(m[2]) * 10)
    if (w > 0 && h > 0) return { widthMm: w, heightMm: h }
  }
  return null
}

function dimsEqual(a: { widthMm: number; heightMm: number }, b: { widthMm: number; heightMm: number }, toleranceMm = 1) {
  return Math.abs(a.widthMm - b.widthMm) <= toleranceMm && Math.abs(a.heightMm - b.heightMm) <= toleranceMm
}

/**
 * Finds the option in `options` that corresponds to `previous`.
 * Returns null when nothing matches; callers decide on the fallback.
 */
export function findMatchingSizeOption<T extends SizeLike>(options: T[], previous: SizeLike | null | undefined): T | null {
  if (!Array.isArray(options) || !options.length || !previous) return null

  const rawTitle = String(previous.title || '').trim()
  if (rawTitle) {
    const exact = options.find((o) => String(o?.title || '').trim() === rawTitle)
    if (exact) return exact

    const wanted = normalizeSizeTitle(rawTitle)
    if (wanted) {
      const loose = options.find((o) => normalizeSizeTitle(o?.title) === wanted)
      if (loose) return loose
    }
  }

  const prevDims = sizeDimsMm(previous)
  if (prevDims) {
    const same = options.find((o) => {
      const d = sizeDimsMm(o)
      return !!d && dimsEqual(d, prevDims)
    })
    if (same) return same

    const swapped = { widthMm: prevDims.heightMm, heightMm: prevDims.widthMm }
    const rotated = options.find((o) => {
      const d = sizeDimsMm(o)
      return !!d && dimsEqual(d, swapped)
    })
    if (rotated) return rotated
  }

  return null
}

/** Product default first, otherwise the first option. */
export function defaultSizeOption<T extends SizeLike>(options: T[]): T | null {
  if (!Array.isArray(options) || !options.length) return null
  return options.find((o) => !!o?.isDefault) || options[0] || null
}
